/**
 * Hook to check if current user has admin privileges.
 * Phase 11.5a - Admin Foundations
 */
import { useState, useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { apiGetAuth, waitForAuthReady } from '../api'

interface UserMeResponse {
  id: number
  email: string
  is_superuser: boolean
}

const clerkEnabled = !!(import.meta as any).env?.VITE_CLERK_PUBLISHABLE_KEY
const isDev = (import.meta as any).env?.DEV || (import.meta as any).env?.MODE === 'development'

export function useIsAdmin(): { isAdmin: boolean; isLoading: boolean } {
  const [isAdmin, setIsAdmin] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const auth = clerkEnabled ? useAuth() : { isSignedIn: false, isLoaded: true }
  const { isSignedIn, isLoaded } = auth

  useEffect(() => {
    // In dev mode without Clerk, grant admin access for testing
    if (!clerkEnabled) {
      setIsAdmin(isDev)
      setIsLoading(false)
      return
    }

    if (!isLoaded) {
      setIsLoading(false)
      return
    }

    if (!isSignedIn) {
      setIsAdmin(false)
      setIsLoading(false)
      return
    }

    const checkAdmin = async () => {
      try {
        await waitForAuthReady()
        const user = await apiGetAuth<UserMeResponse>('/user/me')
        setIsAdmin(user.is_superuser)
      } catch (error) {
        console.error('Failed to check admin status:', error)
        setIsAdmin(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkAdmin()
  }, [isSignedIn, isLoaded])

  return { isAdmin, isLoading }
}
